import { useAnnotateGsDiscrepancyMutation, type GsDiscrepancy } from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { decisionLabel, fmtMoney } from '@shared/lib/smetaLabels';
import { discrTone, kindLabel } from '../helpers';
import { DecisionBtns, Muted, Table } from '../styles';
import { DecisionSaveCaption } from '../../DecisionSaveError';
import { useDecisionSave } from '../../decisionSave';

export function DiscrepanciesTable({
  items,
  checkId,
}: {
  items: GsDiscrepancy[];
  checkId: string;
}) {
  const [annotate, { isLoading }] = useAnnotateGsDiscrepancyMutation();
  const { error: saveError, save } = useDecisionSave();
  const { can_write } = useAgentPermission('smeta');
  if (items.length === 0) {
    return <Muted $mt={8}>Расхождений не найдено.</Muted>;
  }
  return (
    <Table>
      <DecisionSaveCaption message={saveError} />
      <thead>
        <tr>
          <th>Важность</th>
          <th>Вид</th>
          <th>Шифр / наим.</th>
          <th className="num">ГП, ₽</th>
          <th className="num">СП, ₽</th>
          <th className="num">Δ, ₽</th>
          <th>Сообщение</th>
          <th>Решение</th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id}>
            <td>
              <Badge $tone={discrTone(d.severity)}>{d.severity}</Badge>
            </td>
            <td>{kindLabel(d.kind)}</td>
            <td>
              {d.code ?? '—'}
              {d.name ? <Muted>{d.name}</Muted> : null}
            </td>
            <td className="num">{fmtMoney(d.gp_total)}</td>
            <td className="num">{fmtMoney(d.sp_total)}</td>
            <td className="num">{fmtMoney(d.delta_total)}</td>
            <td>{d.message}</td>
            <td>
              {d.decision && d.decision !== 'pending' ? (
                <Badge
                  $tone={
                    d.decision === 'accepted'
                      ? 'success'
                      : d.decision === 'rejected'
                        ? 'neutral'
                        : 'warning'
                  }
                >
                  {decisionLabel(d.decision)}
                </Badge>
              ) : (
                <DecisionBtns>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'accepted' }))}
                  >
                    Принять
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'disputed' }))}
                  >
                    Спорно
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => void save(() => annotate({ id: d.id, checkId, decision: 'rejected' }))}
                  >
                    Отклонить
                  </Button>
                </DecisionBtns>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
